// src/components/Dashboard/CoachDashboard/PlayerSpotlightWidget.jsx
// Highlights players needing attention or recognition

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { 
  FaUsers, FaFire, FaExclamationTriangle, FaUserPlus, 
  FaTrophy, FaArrowDown, FaArrowRight
} from 'react-icons/fa';
import styles from './PlayerSpotlightWidget.module.css';

const PlayerSpotlightWidget = ({ program, userProfile, refreshTrigger }) => {
  const navigate = useNavigate();
  const [players, setPlayers] = useState([]);
  const [spotlights, setSpotlights] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch team players
  useEffect(() => {
    const fetchPlayers = async () => {
      if (!program?._id || !userProfile?._id) {
        setPlayers([]);
        setSpotlights([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
        const auth = getAuth();
        const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;
        
        const headers = token ? {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        } : {};
        
        // Fetch teams
        const teamsResponse = await fetch(`${backendUrl}/api/teams/program/${program._id}`, { headers });
        if (!teamsResponse.ok) {
          setSpotlights([]);
          return;
        }

        const teams = await teamsResponse.json();
        const teamsArray = Array.isArray(teams) ? teams : [teams];
        
        // Filter teams where user is coach
        const myTeams = teamsArray.filter(team => {
          const isHeadCoach = team.coach?.userId === userProfile._id || 
                              team.coach?.userId?._id === userProfile._id;
          const isAssistantCoach = team.assistantCoaches?.some(ac => 
            ac.userId === userProfile._id || ac.userId?._id === userProfile._id
          );
          return isHeadCoach || isAssistantCoach;
        });

        // Collect all players from coach's teams
        const allPlayers = [];
        myTeams.forEach(team => {
          (team.players || []).forEach(player => {
            allPlayers.push({
              ...player,
              teamName: team.name,
              teamId: team._id
            });
          });
        });

        setPlayers(allPlayers);

        // Generate spotlight cards
        const spotlightCards = generateSpotlights(allPlayers);
        setSpotlights(spotlightCards);
      } catch (error) {
        console.error('Error fetching players:', error);
        setSpotlights([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPlayers();
  }, [program?._id, userProfile?._id, refreshTrigger]);

  // Generate spotlight insights
  const generateSpotlights = (playersList) => {
    const cards = [];

    if (playersList.length === 0) return cards;

    // New players (joined recently)
    const newPlayers = playersList.filter(p => {
      const joinDate = new Date(p.assignedAt || p.createdAt);
      const daysAgo = (new Date() - joinDate) / (1000 * 60 * 60 * 24);
      return daysAgo <= 14;
    });

    if (newPlayers.length > 0) {
      cards.push({
        id: 'new',
        type: 'new',
        icon: FaUserPlus,
        label: 'New to Team',
        playerName: getPlayerName(newPlayers[0]),
        detail: 'Just joined',
        color: '#3b82f6'
      });
    }

    // Add placeholder spotlights for demo
    // In real implementation, these would come from analytics data
    if (playersList.length > 0) {
      cards.push({
        id: 'top',
        type: 'top',
        icon: FaTrophy,
        label: 'Top Player',
        playerName: getPlayerName(playersList[0]),
        detail: 'Most active',
        color: '#fbbf24'
      });
    }

    if (playersList.length > 1) {
      cards.push({
        id: 'streak',
        type: 'streak',
        icon: FaFire,
        label: 'On Fire',
        playerName: getPlayerName(playersList[Math.min(1, playersList.length - 1)]),
        detail: 'Great form',
        color: '#ef4444'
      });
    }

    return cards.slice(0, 5);
  };

  const getPlayerName = (player) => {
    if (player.userId?.firstName) {
      return `${player.userId.firstName} ${player.userId.lastName || ''}`.trim();
    }
    return player.roleName || 'Player';
  };

  const handleViewRoster = () => {
    if (program) {
      navigate(`/program/${program._id}/teams`);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <FaUsers className={styles.headerIcon} />
          <h3 className={styles.headerTitle}>Player Spotlight</h3>
        </div>
        <div className={styles.loadingState}>
          <div className={styles.loadingSpinner}></div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <FaUsers className={styles.headerIcon} />
        <h3 className={styles.headerTitle}>Player Spotlight</h3>
        <span className={styles.playerCount}>{players.length} Players</span>
      </div>

      <div className={styles.content}>
        {spotlights.length === 0 ? (
          <div className={styles.emptyState}>
            <span className={styles.emptyIcon}>👤</span>
            <p>No player insights available</p>
            <span className={styles.emptyHint}>Insights will appear as activity increases</span>
          </div>
        ) : (
          <div className={styles.spotlightGrid}>
            {spotlights.map(spotlight => (
              <div 
                key={spotlight.id} 
                className={styles.spotlightCard}
                style={{ '--spotlight-color': spotlight.color }}
              >
                <div className={styles.spotlightIcon}>
                  <spotlight.icon />
                </div>
                <div className={styles.spotlightInfo}>
                  <span className={styles.spotlightLabel}>{spotlight.label}</span>
                  <span className={styles.spotlightPlayer}>{spotlight.playerName}</span>
                  <span className={styles.spotlightDetail}>{spotlight.detail}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={styles.footer}>
        <button className={styles.footerLink} onClick={handleViewRoster}>
          View Full Roster <FaArrowRight />
        </button>
      </div>
    </div>
  );
};

export default PlayerSpotlightWidget;

